import { useState, useEffect, useRef, useCallback } from 'react';
import { useStopwatch2 } from './useStopwatch';
import { recordingStatusEnum } from './useAudioRecorder';

/**
 * Speech-optimized audio recorder that captures 16 kHz mono WAV.
 *
 * Azure Pronunciation Assessment needs ≥ 20 dB SNR and works best with
 * uncompressed PCM (WAV) at 16 kHz.  The default `useAudioRecorder` hook
 * records lossy MPEG via polyfill, producing ~5 dB SNR — too low for
 * reliable scoring.
 *
 * This hook bypasses the polyfill entirely: it opens an AudioContext at
 * 16 kHz, pipes the mic through a ScriptProcessorNode, and encodes the
 * collected Float32 samples into a standard 16-bit PCM WAV blob.
 */

type tUseSpeechAudioRecorderReturnType = [
  recordingStatusEnum,
  number | undefined,
  Blob | null,
  {
    startRecording: () => void;
    pauseRecording: () => void;
    finishRecording: () => void;
    resetRecording: () => void;
    getVolume: () => number;
  }
];

type tUseSpeechAudioRecorderArgs = {
  onRecordChange?: (audioUrl: string | null, blob?: Blob | null) => void;
  onError?: (errorMessage: string) => void;
  setDuration?: (seconds: number) => void;
};

const TARGET_SAMPLE_RATE = 16000;
const BUFFER_SIZE = 4096;

// ---------------------------------------------------------------------------
// WAV encoder
// ---------------------------------------------------------------------------

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const numSamples = samples.length;
  const buffer = new ArrayBuffer(44 + numSamples * 2);
  const view = new DataView(buffer);

  // RIFF header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + numSamples * 2, true);
  writeString(view, 8, 'WAVE');

  // fmt  sub-chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // sub-chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample

  // data sub-chunk
  writeString(view, 36, 'data');
  view.setUint32(40, numSamples * 2, true);

  const offset = 44;
  for (let i = 0; i < numSamples; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSpeechAudioRecorder({
  onRecordChange,
  onError,
  setDuration
}: tUseSpeechAudioRecorderArgs): tUseSpeechAudioRecorderReturnType {
  const [status, setStatus] = useState(recordingStatusEnum.EMPTY);
  const statusRef = useRef(recordingStatusEnum.EMPTY);
  const [stopwatch, seconds] = useStopwatch2();
  const audioBlob = useRef<Blob | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const fftBinsRef = useRef<Uint8Array | null>(null);
  const chunksRef = useRef<Float32Array[]>([]);
  const audioUrlRef = useRef<string | null>(null);
  // Latch for one startRecording() call; it only spans the async setup here because the
  // graph is per-hook (the `.mobile` variant latches the whole session — native
  // singleton).
  const isStartingRef = useRef(false);
  // False between unmount and (in StrictMode) the next mount. Async work started
  // before unmount must not touch state or leave a hot mic behind.
  const isMountedRef = useRef(true);

  function _setStatus(s: recordingStatusEnum) {
    setStatus(s);
    statusRef.current = s;
  }

  function revokeAudioUrl() {
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  }

  const cleanup = useCallback(() => {
    // Drop the audio callback first: a disconnected ScriptProcessor can still
    // hold a stale closure that would keep appending to chunksRef.
    if (processorRef.current) {
      processorRef.current.onaudioprocess = null;
    }
    processorRef.current?.disconnect();
    sourceRef.current?.disconnect();
    analyserRef.current?.disconnect();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    // close() rejects on a context the OS already invalidated (interruption, route
    // change) — nothing can be done about it and the rejection is only noise.
    audioContextRef.current?.close().catch(() => {});
    processorRef.current = null;
    sourceRef.current = null;
    analyserRef.current = null;
    streamRef.current = null;
    audioContextRef.current = null;
    fftBinsRef.current = null;
  }, []);

  // ---- public API ----------------------------------------------------------

  const startRecording = useCallback(async () => {
    // No-op a double tap: a second tap before the status flips to RECORDING would
    // overwrite streamRef/audioContextRef (leaking a mic cleanup() can no longer reach)
    // and both onaudioprocess closures would push into the same chunksRef, producing one
    // interleaved WAV. Latch before the first await.
    if (isStartingRef.current) return;
    isStartingRef.current = true;
    try {
      // Inside the try, so a throw here cannot leave the latch stuck.
      cleanup();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: TARGET_SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // Assign the stream/context to refs as soon as they exist so a failure
      // below (resume/node setup) releases them via cleanup() in the catch.
      streamRef.current = stream;

      // The hook can unmount while getUserMedia is pending (permission prompt open, the
      // student leaves the task, then grants). The unmount cleanup already ran against
      // null refs, so without this the mic stays hot until reload.
      if (!isMountedRef.current) {
        cleanup();
        return;
      }

      const audioContext = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
      audioContextRef.current = audioContext;
      // iOS mobile-web can create the context 'suspended'; without resume the
      // ScriptProcessor never fires and the recording is silent/empty.
      await audioContext.resume();

      // resume() is awaited too, so the same unmount race applies.
      if (!isMountedRef.current) {
        cleanup();
        return;
      }

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 32;

      chunksRef.current = [];

      processor.onaudioprocess = (e) => {
        if (statusRef.current === recordingStatusEnum.RECORDING) {
          chunksRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0)));
        }
      };

      source.connect(analyser);
      analyser.connect(processor);
      processor.connect(audioContext.destination);

      audioContextRef.current = audioContext;
      streamRef.current = stream;
      processorRef.current = processor;
      sourceRef.current = source;
      analyserRef.current = analyser;
      fftBinsRef.current = new Uint8Array(analyser.frequencyBinCount);

      _setStatus(recordingStatusEnum.RECORDING);
      stopwatch.start();
    } catch (err) {
      // Release the mic/context if setup failed after getUserMedia.
      cleanup();
      onError?.(err instanceof Error ? err.toString() : String(err));
    } finally {
      isStartingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanup, onError, stopwatch]);

  const finishRecording = useCallback(() => {
    stopwatch.pause();
    setDuration?.(seconds ?? 0);

    // Merge PCM chunks → WAV blob
    const totalLength = chunksRef.current.reduce((acc, c) => acc + c.length, 0);

    // No audio captured (e.g. suspended context / permission race) — recover to
    // EMPTY instead of emitting a 0-length WAV the backend would reject.
    if (totalLength === 0) {
      chunksRef.current = [];
      cleanup();
      onError?.('Recording produced no audio data');
      _setStatus(recordingStatusEnum.EMPTY);
      stopwatch.reset();
      return;
    }

    const merged = new Float32Array(totalLength);
    let offset = 0;
    chunksRef.current.forEach((chunk) => {
      merged.set(chunk, offset);
      offset += chunk.length;
    });
    chunksRef.current = [];

    const sampleRate = audioContextRef.current?.sampleRate ?? TARGET_SAMPLE_RATE;
    const wavBlob = encodeWav(merged, sampleRate);
    audioBlob.current = wavBlob;

    revokeAudioUrl();
    const url = URL.createObjectURL(wavBlob);
    audioUrlRef.current = url;
    onRecordChange?.(url, wavBlob);

    cleanup();
    _setStatus(recordingStatusEnum.FINISHED);
    stopwatch.reset();
  }, [cleanup, onRecordChange, onError, setDuration, seconds, stopwatch]);

  const pauseRecording = useCallback(() => {
    _setStatus(recordingStatusEnum.PAUSED);
    stopwatch.pause();
  }, [stopwatch]);

  const resetRecording = useCallback(() => {
    stopwatch.pause();
    stopwatch.reset();
    chunksRef.current = [];
    audioBlob.current = null;
    revokeAudioUrl();
    cleanup();
    _setStatus(recordingStatusEnum.EMPTY);
  }, [cleanup, stopwatch]);

  const getVolume = useCallback(() => {
    if (!analyserRef.current || !fftBinsRef.current) return 0;
    analyserRef.current.getByteFrequencyData(fftBinsRef.current);
    let max = 0;
    for (let i = 0; i < fftBinsRef.current.length; i++) {
      const v = fftBinsRef.current[i] / 2;
      if (v > max) max = v;
    }
    return max;
  }, []);

  // Cleanup on unmount. The mount flag is cleared here so a startRecording() still
  // awaiting getUserMedia/resume releases what it allocates instead of orphaning it.
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      cleanup();
      revokeAudioUrl();
    };
  }, [cleanup]);

  return [
    status,
    seconds,
    audioBlob.current,
    {
      startRecording,
      pauseRecording,
      finishRecording,
      resetRecording,
      getVolume
    }
  ];
}
